type PreparationFactory<T> = () => Promise<T>;

export type InterviewEntryStartIntent = 'activate_fresh' | 'resume_existing';
export type InterviewRecoveredSessionMode = 'new' | 'resume';
export type InterviewSessionStartupMode = InterviewRecoveredSessionMode | null;

export type InterviewSessionStartupNavigation = {
  sessionMode: InterviewSessionStartupMode;
  observabilityTraceId: string;
  startupStartedAt: number | null;
  reusePreparedMedia: boolean;
};

type BrowserHistoryLike = {
  state: unknown;
  replaceState: (data: unknown, unused: string, url?: string | URL | null) => void;
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const hasOwn = (value: Record<string, unknown>, key: string) => (
  Object.prototype.hasOwnProperty.call(value, key)
);

export const captureInterviewSessionStartupNavigation = (
  routeState: unknown,
): InterviewSessionStartupNavigation => {
  const source = isRecord(routeState) ? routeState : {};
  const sessionMode = source.sessionMode === 'new'
    ? 'new'
    : source.sessionMode === 'resume'
    ? 'resume'
    : null;
  const rawStartedAt = source.startupStartedAt;
  const startupStartedAt = rawStartedAt !== null
    && rawStartedAt !== undefined
    && String(rawStartedAt).trim() !== ''
    && Number.isFinite(Number(rawStartedAt))
    ? Number(rawStartedAt)
    : null;

  return {
    sessionMode,
    observabilityTraceId: String(source.observabilityTraceId || '').trim(),
    startupStartedAt,
    reusePreparedMedia: Boolean(source.reusePreparedMedia),
  };
};

export const consumeInterviewSessionStartupHistoryState = ({
  history,
  currentUrl,
}: {
  history: BrowserHistoryLike;
  currentUrl?: string;
}) => {
  const historyState = isRecord(history.state) ? history.state : null;
  const routeState = historyState && isRecord(historyState.usr) ? historyState.usr : null;
  if (!historyState || !routeState) return false;

  const sanitizedRouteState = { ...routeState };
  let changed = false;

  if (sanitizedRouteState.sessionMode === 'new') {
    delete sanitizedRouteState.sessionMode;
    changed = true;
  }

  ['observabilityTraceId', 'startupStartedAt', 'reusePreparedMedia'].forEach((key) => {
    if (!hasOwn(sanitizedRouteState, key)) return;
    delete sanitizedRouteState[key];
    changed = true;
  });

  if (!changed) return false;

  try {
    history.replaceState(
      { ...historyState, usr: sanitizedRouteState },
      '',
      currentUrl,
    );
    return true;
  } catch (_) {
    return false;
  }
};

export const resolveEffectiveInterviewSessionMode = ({
  initialRouteSessionMode,
  canonicalSessionMode,
}: {
  initialRouteSessionMode: InterviewSessionStartupMode;
  canonicalSessionMode?: string | null;
}): InterviewRecoveredSessionMode => {
  if (initialRouteSessionMode === 'new') return 'new';

  const normalizedCanonicalMode = String(canonicalSessionMode || '').trim().toLowerCase();
  if (normalizedCanonicalMode === 'new') return 'new';
  if (normalizedCanonicalMode === 'resume') return 'resume';
  if (initialRouteSessionMode === 'resume') return 'resume';

  // InterviewSession hydration without an explicit live fresh intent is a revisit.
  return 'resume';
};

type LostStartResponseRecoveryInput = {
  interviewStatus?: string | null;
  answeredCount?: number | null;
  backendSessionMode?: string | null;
  startIntent: InterviewEntryStartIntent;
  recoveryCandidateName?: string | null;
  entryCandidateName?: string | null;
};

const normalizeCandidateName = (value?: string | null) => String(value || '').trim();

export const resolveLostStartResponseRecovery = ({
  interviewStatus,
  answeredCount,
  backendSessionMode,
  startIntent,
  recoveryCandidateName,
  entryCandidateName,
}: LostStartResponseRecoveryInput): {
  canRecover: boolean;
  sessionMode: InterviewRecoveredSessionMode;
  candidateName: string;
} => {
  const normalizedStatus = String(interviewStatus || '').trim().toLowerCase();
  const normalizedAnsweredCount = Number(answeredCount);
  const hasAuthoritativeAnsweredCount = answeredCount !== null
    && answeredCount !== undefined
    && String(answeredCount).trim() !== ''
    && Number.isFinite(normalizedAnsweredCount);
  const safeAnsweredCount = hasAuthoritativeAnsweredCount
    ? Math.max(0, normalizedAnsweredCount)
    : null;
  const normalizedBackendMode = String(backendSessionMode || '').trim().toLowerCase();
  const isFreshActivationRecovery = normalizedStatus === 'in_progress'
    && safeAnsweredCount === 0
    && startIntent === 'activate_fresh';

  return {
    canRecover: normalizedStatus === 'in_progress',
    sessionMode: isFreshActivationRecovery
      ? 'new'
      : normalizedBackendMode === 'new' && safeAnsweredCount === 0 && startIntent !== 'resume_existing'
      ? 'new'
      : 'resume',
    candidateName: normalizeCandidateName(recoveryCandidateName)
      || normalizeCandidateName(entryCandidateName),
  };
};

const inFlightPreparations = new Map<string, Promise<unknown>>();

const buildPreparationKey = (outsourceId: number, jobId: number) => `${outsourceId}:${jobId}`;

export const getOrCreateInterviewStartupPreparation = <T>({
  outsourceId,
  jobId,
  prepare,
}: {
  outsourceId: number;
  jobId: number;
  prepare: PreparationFactory<T>;
}): { promise: Promise<T>; reused: boolean } => {
  const key = buildPreparationKey(outsourceId, jobId);
  const current = inFlightPreparations.get(key) as Promise<T> | undefined;
  if (current) return { promise: current, reused: true };

  const promise = prepare();
  inFlightPreparations.set(key, promise);

  const release = () => {
    if (inFlightPreparations.get(key) === promise) {
      inFlightPreparations.delete(key);
    }
  };
  promise.then(release, release);

  return { promise, reused: false };
};

export const clearInterviewStartupPreparationsForTests = () => {
  inFlightPreparations.clear();
};
